/**
 * Import OL classes
 */
import XYZ from 'ol/source/XYZ';
import TileLayer from 'ol/layer/Tile';
import {Map, View} from 'ol';
// import View from 'ol/View';
import {fromLonLat} from 'ol/proj';
// import Map from 'ol/Map';
import {defaults as controlDefaults} from 'ol/control';
import ScaleLine from 'ol/control/ScaleLine';
import {defaults as interactionDefaults} from 'ol/interaction';
import DragRotate from 'ol/interaction/DragRotate';
import {getCenter as getExtentCenter, getWidth as getExtentWidth, getHeight as getExtentHeight} from 'ol/extent';
import ImageLayer from 'ol/layer/Image';
import Static from 'ol/source/ImageStatic';
import VectorLayer from 'ol/layer/Vector';

/**
 * Import OL-EXT classes
 */
import Hover from 'ol-ext/interaction/hover';

/**
 * Import own classes
 */
import {RotateControl} from '/src/modules/Controls/rotateControl/rotateControl';
import {ContextControl, showContext} from '/src/modules/Controls/context/context';
import {NavigatorControl} from '/src/modules/Controls/navigator/navigator';
import {SideNavigationControl} from '/src/modules/Controls/sideNavigation/sideNavigation';
import {focusOn} from '/src/modules/features/featureControl';
import {featureStyleCall} from '/src/modules/features/featureStyle';
import {printPdf} from '/src/modules/capture/capture';

/**
 * Import global variables
 */
import { lang, exhibitionConfig, baseConfig, messeId, fileKey } from '/main';

let map;

let browserService;

/**
 * @param {*} service
 */
export function setBrowserService(service) {
  browserService = service;
}

/**
 * Load base configuration and set up the map
 *
 * @param {object} baseConfig
 * @param {object} exhibitionConfig
 * @return {*}
 */
export async function createBaseMap(baseConfig, exhibitionConfig) {
  let mapSource;
  let baseLayer = null;
  let mapView;
  let imageOverlay = null;

  try {
    let mapProperties = baseConfig['mapProperties'];
    let viewProperties = baseConfig['viewProperties'];

    let attributionText = '';

    if (exhibitionConfig['menu']['display'] === false) {
      if (exhibitionConfig['menu']['footer']['imprint_' + lang['id']] !== null && exhibitionConfig['menu']['footer']['imprint_' + lang['id']] !== '') {
        attributionText += '<a href="' + exhibitionConfig['menu']['footer']['imprint_' + lang['id']] + '" target="_blank">' + lang['sideNavigationImprint'] + '</a> | ';
      }

      if (exhibitionConfig['menu']['footer']['dataPrivacy_' + lang['id']] !== null && exhibitionConfig['menu']['footer']['dataPrivacy_' + lang['id']] !== '') {
        attributionText += '<a href="' + exhibitionConfig['menu']['footer']['dataPrivacy_' + lang['id']] + '" target="_blank">' + lang['sideNavigationDataPrivacy'] + '</a> | ';
      }

      if (exhibitionConfig['menu']['footer']['termsAndConditions_' + lang['id']] !== null && exhibitionConfig['menu']['footer']['termsAndConditions_' + lang['id']] !== '') {
        attributionText += '<a href="' + exhibitionConfig['menu']['footer']['termsAndConditions_' + lang['id']] + '" target="_blank">' + lang['sideNavigationTermsAndConditions'] + '</a> | ';
      }

      if (exhibitionConfig['menu']['footer']['help_' + lang['id']] !== null && exhibitionConfig['menu']['footer']['help_' + lang['id']] !== '') {
        attributionText += '<a href="' + exhibitionConfig['menu']['footer']['help_' + lang['id']] + '" target="_blank">' + lang['sideNavigationHelp'] + '</a> | ';
      }
    }

    attributionText += 'Powered by Flayr | Map &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors';
    // mapSource = new XYZ({
    //   crossOrigin: 'anonymous',
    //   url: 'https://tileserver.nfm-servicecenter.de/{z}/{x}/{y}',
    //   attributions: attributionText,
    //   projection: 'EPSG:3857',
    // });

    /**
     * Create new base layer
     * Check if device has an internet connection (with navigator.onLine)
     */
    if (navigator.onLine === true) {
      // baseLayer = new TileLayer({
      //   source: mapSource,
      //   preload: mapProperties['preload'] === -1 ? Infinity : mapProperties['preload'],
      //   baseLayer: mapProperties['baseLayer'],
      //   opacity: mapProperties['opacity'],
      //   title: mapProperties['title'],
      //   minResolution: mapProperties['minResolution'] === null ? undefined : mapProperties['minResolution'],
      //   maxResolution: mapProperties['maxResolution'] === null ? undefined : mapProperties['maxResolution'],
      //   zIndex: mapProperties['zIndex'],
      // });
    }

    if (
      typeof mapProperties['fallbackImage'] !== 'undefined'
      && mapProperties['fallbackImage'] !== ''
      && mapProperties['fallbackImage'] !== undefined
    ) {
      imageOverlay = new ImageLayer({
        source: new Static({
          url: typeof exhibitionConfig['mapProperties'] !== 'undefined' && exhibitionConfig['mapProperties']['fallbackImage'] != null && exhibitionConfig['mapProperties']['fallbackImage'] != '' ? exhibitionConfig['mapProperties']['fallbackImage'] : mapProperties['fallbackImage'],
          imageExtent: typeof exhibitionConfig['mapProperties'] !== 'undefined' && exhibitionConfig['mapProperties']['fallbackImageExtent'] && exhibitionConfig['mapProperties']['fallbackImageExtent'].length > 0 ? exhibitionConfig['mapProperties']['fallbackImageExtent'] : mapProperties['fallbackImageExtent'],
          crossOrigin: '',
          projection: 'EPSG:3857',
          imageSize: typeof exhibitionConfig['mapProperties'] !== 'undefined' && exhibitionConfig['mapProperties']['fallbackImageSize'] && exhibitionConfig['mapProperties']['fallbackImageSize'].length > 0 ? exhibitionConfig['mapProperties']['fallbackImageSize'] : mapProperties['fallbackImageSize'],
        }),
      });
    }

    /**
     * Create new view
     */
    let startCoordinate = null;
    if (typeof exhibitionConfig['viewProperties'] !== 'undefined' && exhibitionConfig['viewProperties']['startCoordinate'] &&exhibitionConfig['viewProperties']['startCoordinate'].length > 0) {
      startCoordinate = exhibitionConfig['viewProperties']['startCoordinate'];
    } else {
      startCoordinate = viewProperties['startCoordinate'];
    }

    let baseCoordinate = null;
    if (typeof exhibitionConfig['viewProperties'] !== 'undefined' && exhibitionConfig['viewProperties']['baseCoordinate'] && exhibitionConfig['viewProperties']['baseCoordinate'].length > 0) {
      baseCoordinate = exhibitionConfig['viewProperties']['baseCoordinate'];
    } else {
      baseCoordinate = viewProperties['baseCoordinate'];
    }

    mapView = new View({
      center: fromLonLat(startCoordinate),
      zoom: viewProperties['initialZoom'],
      minZoom: viewProperties['minZoom'] === null ? undefined : viewProperties['minZoom'],
      maxZoom: viewProperties['maxZoom'] === null ? undefined : viewProperties['maxZoom'],
      zoomFactor: viewProperties['zoomFactor'],
      extent: viewProperties['extent'] === null ? undefined : viewProperties['extent'],
      projection: 'EPSG:3857',
      rotation: 0,
    });

    mapView.set('baseCenter', fromLonLat(baseCoordinate));
    mapView.set('baseZoom', viewProperties['baseZoom']);
    mapView.set('rotationDirection', viewProperties['rotationDirection']);

    /**
     * Create new map
     */
    map = new Map({
      target: 'flayrMap',
      controls: new controlDefaults({
        attributionOptions: {
          target: document.getElementById('control-attribution'),
          collapsible: false,
        },
        rotateOptions: {
          target: document.getElementById('control-rotation'),
          label: '',
          tipLabel: lang['rotateControlNorthRotationTitle'],
          duration: 1000,
        },
        zoomOptions: {
          target: document.getElementById('control-zoom'),
        },
      }).extend([
        new ScaleLine({
          target: document.getElementById('control-scaleLine'),
        }),
        new RotateControl({
          direction: 'left',
          target: document.getElementById('control-rotation-left'),
        }),
        new RotateControl({
          direction: 'right',
          target: document.getElementById('control-rotation-right'),
        }),
        new ContextControl({
          target: document.getElementById('control-context'),
        }),
        new NavigatorControl({
          target: document.getElementById('control-navigator'),
        }),
      ]),
      interactions: new interactionDefaults().extend([
        new DragRotate(),
        new Hover({
          cursor: 'pointer',
        }),
      ]),
      view: mapView,
    });

    if (exhibitionConfig['menu']['display'] === true) {
      map.addControl(new SideNavigationControl({
        target: document.getElementById('control-sidenav'),
      }));
    }

    if (typeof exhibitionConfig['core']['fairground'] !== 'undefined' && exhibitionConfig['core']['fairground'] == 'koeln') {
      map.addControl(new RotateControl({
        direction: 'reset',
        target: document.getElementById('control-reset-rotation'),
      }));
    }

    if (imageOverlay !== null) {
      map.addLayer(imageOverlay);
    }

    if (baseLayer !== null) {
      map.addLayer(baseLayer);
    }

    return map;
  } catch (err) {
    throw new Error('Base error: ' + err);
  }
}

/**
 * Read all parameters from the URL which was used to call the map
 *
 * @param {string} parameterName
 * @return {?string} result
 */
export function findGetParameter(parameterName) {
  let result = null;
  let tmp = [];
  let items;
  let decodedString;

  try {
    let queryString = browserService.getURIParameters();

    let match = findHashParameter(queryString);

    let hash = decodeURIComponent(match);

    decodedString = atob(hash);

    items = decodedString.split('&');

    for (let index = 0; index < items.length; index++) {
      tmp = items[index].split('=');
      if (tmp[0] === parameterName) {
        result = decodeURIComponent(tmp[1]);
      }
    }
    return result;
  } catch (error) {
    if (error['name'] === 'InvalidCharacterError') {
      throw new Error(lang['errorInvalidHash']);
    }
  }
}

/**
 * @param {string} queryString
 * @return {*}
 */
export function findHashParameter(queryString) {
  let regex = new RegExp('[?&]?[^a-zA-Z]hash=([a-zA-Z0-9=%]+)');

  let match = queryString.match(regex);

  if (match !== null && match !== '') {
    return match[1];
  } else {
    throw new Error('Fehler');
  }
}

/**
 * @param {string} getParam
 */
export function showFeatureByParameter(getParam) {
  let featureExtent;
  let extentCenter;
  let rotate;
  let found = false;
  let boothFeature;
  let baseFeature = null;

  map.getLayers().forEach(function(layer) {
    if (typeof layer.getSource().forEachFeature === 'function') {
      layer.getSource().forEachFeature(function(feature) {
        if (feature.get('type') === 'Stand') {
          if (feature.get('quotelineid') === getParam) {
            boothFeature = feature;
            found = true;
            featureExtent = feature.getGeometry().getExtent();
            extentCenter = getExtentCenter(featureExtent);
            map.getLayers().forEach((baseLayer) => {
              if (baseLayer instanceof VectorLayer) {
                if (baseLayer.getSource().getFeatureById(feature.get('correspondingId')) !== null) {
                  baseFeature = baseLayer.getSource().getFeatureById(feature.get('correspondingId'));
                }
              }
            });
            if (typeof exhibitionConfig['core']['rotateOnFocus'] !== 'undefined' && exhibitionConfig['core']['rotateOnFocus'] === false) {
              rotate = false;
            } else {
              if (baseFeature !== null && typeof baseFeature.get('rotation') !== 'undefined') {
                rotate = baseFeature.get('rotation');
              } else {
                rotate = getExtentWidth(featureExtent) < getExtentHeight(featureExtent);
              }
            }

            feature.setStyle(featureStyleCall(feature, map.getView().getResolution(), true));

            focusOn(extentCenter, rotate, 42, null, false);
            showContext(feature);
          }
        }
      });
    }
  });

  if (found === false) {
    throw new Error(lang['errorNoBoothForBoothId']);
  } else {
    /**
     * Create a button for jumping back to booth
     * @type {HTMLDivElement}
     */
    let backToBoothButton = document.createElement('div');
    backToBoothButton.className = 'back-to-booth-button';
    backToBoothButton.id = 'back-to-booth-button';
    backToBoothButton.innerHTML = '<i class="fas fa-reply"></i> ' + lang['buttonBackToBooth'] + ' <b>' + boothFeature.get('number') + '</b>';

    backToBoothButton.addEventListener('click', () => {
      showContext(boothFeature);

      focusOn(extentCenter, rotate, 42, null, true);
    }, {passive: true});

    document.getElementById('buttonContainer').appendChild(backToBoothButton);

    /**
     * Add print functionality
     */
    let printButton = document.createElement('div');
    printButton.className = 'printOffer';
    printButton.id = 'printOffer';
    printButton.innerHTML = '<i class="fas fa-print"></i> ' + lang['buttonPrintHallPlan'];

    printButton.addEventListener('click', () => {
      showContext(boothFeature);
      let layers = map.getLayers();
      let baseHallFeature;
      layers.forEach((layer) => {
        if (
          layer.get('title') === 'Hallen'
          || layer.get('title') === 'Freigelände'
          || layer.get('title') === 'Eingänge'
          || layer.get('title') === 'Höfe'
          || layer.get('title') === 'Atrium'
          || layer.get('title') === 'ICM'
        ) {
          const hallFeatures = layer.getSource().getFeaturesAtCoordinate(getExtentCenter(boothFeature.getGeometry().getExtent()));
          hallFeatures.forEach((hallFeature) => {
            if (
              hallFeature.get('type') === 'Halle'
              || hallFeature.get('type') === 'Hof'
              || hallFeature.get('type') === 'Freigelände'
              || hallFeature.get('type') === 'Eingang'
              || hallFeature.get('type') === 'Atrium'
              || hallFeature.get('type') === 'ICM'
            ) {
              baseHallFeature = hallFeature;
            }
          });
        }
      });

      if (typeof baseHallFeature !== 'undefined' || fileKey != '') {
        let objectName = '';
        if (typeof baseHallFeature !== 'undefined') {
          let hallExtentCenter = getExtentCenter(baseHallFeature.getGeometry().getExtent());
          let hallExtent = baseHallFeature.getGeometry().getExtent();
          focusOn(hallExtentCenter, rotate, baseHallFeature.get('zoom'), hallExtent, false);

          objectName = baseHallFeature.getId();
        } else {
          objectName = fileKey;
        }

        let exhibitionName;
        for (const entry of baseConfig['idConnection']) {
          if (entry['internalId'] === messeId) {
            exhibitionName = entry['name'];
          }
        }

        const pdfFileResult = async () => {
          if (typeof exhibitionConfig['core']['fairground'] !== 'undefined') {
            return await printPdf(messeId, getParam, objectName, exhibitionConfig['core']['fairground'], lang['id'].toUpperCase());
          } else {
            return await printPdf(messeId, getParam, objectName, baseConfig['core']['fairground'], lang['id'].toUpperCase());
          }
        };

        pdfFileResult().then((result) => {
          if (result.size > 0) {
            const file = new Blob([result], {type: 'application/pdf'});

            if (window.navigator && window.navigator.msSaveOrOpenBlob) {
              window.navigator.msSaveOrOpenBlob(file, (exhibitionName !== null ? exhibitionName + '.' : '') + objectName + '.pdf');
            } else {
              let link = document.createElement('a');
              link.href = URL.createObjectURL(file);
              link.download = (exhibitionName !== null ? exhibitionName + '.' : '') + objectName + '.pdf';
              link.style.display = 'none';
              document.body.appendChild(link);
              link.click();
            }
          } else {
            alert('PDF could not be generated');
          }
        });
      } else {
        focusOn(extentCenter, rotate, 42, null, false);
        alert('Nothing to generate PDF from');
      }
    }, {passive: true});


    document.getElementById('buttonContainer').appendChild(printButton);
  }
}
